import { FaMicroscope } from 'react-icons/fa'
import { LuActivity, LuFlaskConical } from 'react-icons/lu'
import type { ElementType } from 'react'

/**
 * Le catalogue des applications du Lab Studio, en un seul endroit.
 *
 * Il y avait jusqu'ici quatre listes de modules qui ne se parlaient pas : la
 * barre latérale, le panneau d'administration, les cartes du Lab Studio et
 * l'ENUM de la base. Ajouter une application dans le Lab Studio ne la faisait
 * donc apparaître nulle part ailleurs, et le panneau admin proposait encore
 * « Training », qui n'a plus ni page ni route.
 *
 * Ce fichier est la source unique pour les applications du Lab : le Lab Studio
 * y lit ses cartes, l'administration y lit ses interrupteurs. Une entrée
 * ajoutée ici apparaît dans les deux, sans autre geste.
 */
export interface LabApp {
  /**
   * Segment d'URL sous /lab ET suffixe de la permission. Les deux sont
   * volontairement le même identifiant : un seul mot à retenir quand on
   * ajoute une application.
   */
  id: string
  /** Nom commercial, celui qu'on emploie entre nous et devant un CHU. */
  label: string
  /** Ce que l'application fait, en une phrase, pour le panneau admin. */
  description: string
  icon: ElementType
  color: string
  colorScheme: string
  badge?: 'new' | 'beta' | 'updated' | 'coming-soon'
  badgeColor?: string
  /** L'accroche courte des menus (accès rapide de la barre latérale). */
  tagline: string
  /**
   * Couleur d'accent en rgba — actions rapides de l'accueil, pastilles du fil
   * d'activité, séries de l'histogramme. Une seule couleur par application,
   * la même partout, pour qu'on la reconnaisse d'un écran à l'autre.
   */
  accentColor: string
  /**
   * Pour les applications adossées aux Western blots : le module du catalogue
   * (ImmunoBlotSelection) dont les cas lui appartiennent. C'est lui qui permet
   * d'attribuer un cas LabBlotAnalysis à son application — IRIS et PROTIS
   * partagent la même table, seul le test choisi les distingue. Absent pour
   * ALMA, qui a ses propres tables.
   */
  blotModule?: 'iris' | 'protis'
}

export const LAB_APPS: LabApp[] = [
  {
    id: 'parasitology',
    label: 'ALMA',
    description: 'Lecture assistée des lames de parasitologie, paludisme en tête',
    icon: FaMicroscope,
    color: 'purple.500',
    colorScheme: 'purple',
    badge: 'updated',
    badgeColor: 'purple',
    tagline: 'Parasitologie — frottis sanguin',
    accentColor: 'rgba(168, 85, 247, 0.9)',
  },
  {
    id: 'blot-analysis',
    label: 'IRIS',
    description: 'Interprétation des immunoblots : détection des bandes, témoin, verdict tracé',
    icon: LuActivity,
    color: 'teal.500',
    colorScheme: 'teal',
    badge: 'updated',
    badgeColor: 'orange',
    tagline: 'Immunoblot — Western Blot',
    accentColor: 'rgba(13, 148, 136, 0.9)',
    blotModule: 'iris',
  },
  {
    id: 'protis',
    label: 'PROTIS',
    description: 'Caractérisation de profils protéiques par Western blot de recherche',
    icon: LuFlaskConical,
    color: 'cyan.500',
    colorScheme: 'cyan',
    badge: 'new',
    badgeColor: 'cyan',
    tagline: 'Profils protéiques — Western blot',
    accentColor: 'rgba(6, 182, 212, 0.9)',
    blotModule: 'protis',
  },
  // Hématologie et visionneuse DICOM : pas encore ouvertes, donc pas listées.
]

/**
 * Les applications du Lab s'ouvrent par les `permissions` du module `lab`,
 * et non par un type de module à elles.
 *
 * `moduleType` est un ENUM Postgres : lui ajouter une valeur demande une
 * migration et un déploiement du serveur. `permissions` est un tableau JSON
 * que l'API fait déjà transiter dans les deux sens. On y range donc une
 * entrée par application ouverte, préfixée pour ne pas se mélanger aux
 * permissions historiques (`read`, `write`).
 */
/** L'adresse d'une application : son id EST son segment d'URL sous /lab. */
export const labAppPath = (appId: string): string => `/lab/${appId}`

/** L'application qui possède les cas Western blot de ce module de catalogue.
 *  Retombe sur IRIS, le propriétaire historique de toute la table. */
export const labAppOfBlotModule = (blotModule: 'iris' | 'protis'): LabApp =>
  LAB_APPS.find((a) => a.blotModule === blotModule)
  ?? (LAB_APPS.find((a) => a.blotModule === 'iris') as LabApp)

export const LAB_APP_PERMISSION_PREFIX = 'app:'

export const labAppPermission = (appId: string): string =>
  `${LAB_APP_PERMISSION_PREFIX}${appId}`

/**
 * Une application est-elle ouverte à cet utilisateur ?
 *
 * AUCUN RÉGLAGE VAUT TOUT OUVERT. Les comptes existants n'ont que
 * `['read', 'write']` dans leurs permissions : si l'absence d'entrée `app:`
 * fermait tout, les sept utilisateurs actuels perdraient le Lab Studio du jour
 * au lendemain. Le réglage ne commence donc à restreindre qu'à partir du
 * moment où quelqu'un a coché quelque chose dans l'administration.
 */
export const isLabAppEnabled = (
  permissions: string[] | undefined | null,
  appId: string,
): boolean => {
  const scoped = (permissions ?? []).filter((p) => p.startsWith(LAB_APP_PERMISSION_PREFIX))
  if (scoped.length === 0) return true
  return scoped.includes(labAppPermission(appId))
}

/** Les permissions d'application contenues dans une liste, sans le reste. */
export const labAppPermissionsOf = (permissions: string[] | undefined | null): string[] =>
  (permissions ?? []).filter((p) => p.startsWith(LAB_APP_PERMISSION_PREFIX))

/**
 * Pose l'état d'une application dans une liste de permissions, en laissant
 * intactes les permissions qui ne la concernent pas.
 *
 * Premier réglage sur un compte qui n'en avait aucun : cocher une application
 * ne doit pas fermer silencieusement les autres, puisqu'elles étaient toutes
 * ouvertes jusque-là. On matérialise donc d'abord l'état implicite — toutes
 * ouvertes — avant d'appliquer le changement demandé.
 */
export const withLabAppPermission = (
  permissions: string[] | undefined | null,
  appId: string,
  enabled: boolean,
): string[] => {
  const current = permissions ?? []
  const others = current.filter((p) => !p.startsWith(LAB_APP_PERMISSION_PREFIX))
  const scoped = new Set(
    labAppPermissionsOf(current).length > 0
      ? labAppPermissionsOf(current)
      : LAB_APPS.map((a) => labAppPermission(a.id)),
  )
  if (enabled) scoped.add(labAppPermission(appId))
  else scoped.delete(labAppPermission(appId))
  return others.concat(Array.from(scoped))
}
